import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { getStudents, updateStudentEmail, updateStudentPhone, updateStudentCourse, resetStudentPassword, COURSE_OPTIONS } from '../../lib/api';
import type { StudentFull } from '../../lib/api';

type ModalMode = 'email' | 'phone' | 'course' | 'password';

interface ModalState {
  mode: ModalMode;
  student: StudentFull;
}

const StudentLoginManager: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const [students, setStudents] = useState<StudentFull[]>([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [modal, setModal]       = useState<ModalState | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [saving, setSaving]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getStudents(adminPassword)
      .then(res => setStudents(res.students.filter(s => s.status === 'Active')))
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  const openModal = (mode: ModalMode, student: StudentFull) => {
    setModal({ mode, student });
    setInputValue(
      mode === 'email'  ? student.email  :
      mode === 'phone'  ? student.phone  :
      mode === 'course' ? student.course :
      ''
    );
  };

  const closeModal = () => { setModal(null); setInputValue(''); };

  const handleSave = async () => {
    if (!modal || saving) return;
    setSaving(true);
    try {
      const { mode, student } = modal;

      if (mode === 'email') {
        if (!inputValue.trim() || !inputValue.includes('@')) throw new Error('Enter a valid email address');
        await updateStudentEmail(adminPassword, student.studentId, inputValue.trim());
        setStudents(prev => prev.map(s =>
          s.studentId === student.studentId ? { ...s, email: inputValue.trim() } : s
        ));
        toast.success('Email updated');

      } else if (mode === 'phone') {
        if (!inputValue.trim()) throw new Error('Enter a phone number');
        await updateStudentPhone(adminPassword, student.studentId, inputValue.trim());
        setStudents(prev => prev.map(s =>
          s.studentId === student.studentId ? { ...s, phone: inputValue.trim() } : s
        ));
        toast.success('Phone updated');

      } else if (mode === 'course') {
        if (!inputValue.trim()) throw new Error('Select a course');
        await updateStudentCourse(adminPassword, student.studentId, inputValue.trim());
        setStudents(prev => prev.map(s =>
          s.studentId === student.studentId ? { ...s, course: inputValue.trim() } : s
        ));
        toast.success('Course updated');

      } else if (mode === 'password') {
        const res = await resetStudentPassword(adminPassword, student.studentId);
        toast.success(res.neverLoggedIn
          ? `${student.name} hasn't logged in yet, so there's no password to reset — they can log in with email + phone`
          : `Password reset — ${student.name} can now log in with email + phone`);
      }

      closeModal();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const filtered = students.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.email.toLowerCase().includes(search.toLowerCase()) ||
    s.studentId.toLowerCase().includes(search.toLowerCase()) ||
    s.course.toLowerCase().includes(search.toLowerCase()) ||
    s.phone.includes(search)
  );

  const modalTitle =
    modal?.mode === 'email'    ? 'Edit Email' :
    modal?.mode === 'phone'    ? 'Edit Phone Number' :
    modal?.mode === 'course'   ? 'Change Course' :
    'Reset Password';

  return (
    <TrackerLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ color: '#2b2240' }}>Student Login</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage student email, phone number, course, and reset login passwords
          </p>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex gap-3">
          <input
            type="text"
            placeholder="Search by name, email, phone, course, or student ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 text-sm border border-gray-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
          <button
            onClick={load}
            disabled={loading}
            className="text-xs font-semibold px-4 py-2.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {/* Info box */}
        <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 mb-4 text-sm text-blue-700">
          <strong>How student login works:</strong> Students log in using their <strong>email + phone</strong> on first login, then set a password. Use "Reset Password" to let a student re-enter their password if forgotten.
        </div>

        {/* Table */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 py-20 text-center text-gray-400 text-sm">Loading students…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 py-20 text-center text-gray-400 text-sm">No students found.</div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: '#f8f7fc' }} className="border-b border-gray-100">
                    <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Student</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Course</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Email</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Phone</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s, i) => (
                    <tr
                      key={s.studentId}
                      className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/20'}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-sm" style={{ color: '#2b2240' }}>{s.name}</div>
                        <div className="text-xs text-gray-400">{s.studentId}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-600">{s.course || <span className="italic text-gray-300">not set</span>}</span>
                      </td>
                      <td className="px-4 py-3">
                        {s.email
                          ? <span className="text-gray-700">{s.email}</span>
                          : <span className="text-amber-500 italic text-xs font-medium">not set</span>
                        }
                      </td>
                      <td className="px-4 py-3">
                        {s.phone
                          ? <span className="text-gray-700">{s.phone}</span>
                          : <span className="text-amber-500 italic text-xs font-medium">not set</span>
                        }
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          <button onClick={() => openModal('course', s)}
                            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors hover:opacity-80"
                            style={{ backgroundColor: '#fef0e6', color: '#c2410c' }}>
                            Course
                          </button>
                          <button onClick={() => openModal('email', s)}
                            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors hover:opacity-80"
                            style={{ backgroundColor: '#ede9f7', color: '#2b2240' }}>
                            Email
                          </button>
                          <button onClick={() => openModal('phone', s)}
                            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors hover:opacity-80"
                            style={{ backgroundColor: '#e0f2fe', color: '#0369a1' }}>
                            Phone
                          </button>
                          <button onClick={() => openModal('password', s)}
                            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors hover:opacity-80"
                            style={{ backgroundColor: '#fef9c3', color: '#854d0e' }}>
                            Reset PW
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">
              {filtered.length} active student{filtered.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={closeModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 z-10">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-bold" style={{ color: '#2b2240' }}>{modalTitle}</h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              {modal.student.name}
              <span className="text-gray-300 ml-2 text-xs">{modal.student.studentId}</span>
            </p>

            {modal.mode === 'password' ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-5 text-sm text-amber-800">
                <p className="font-semibold mb-1">This will reset the student's password.</p>
                <p>After reset, the student logs in using their <strong>email + phone number</strong> and creates a new password.</p>
              </div>
            ) : modal.mode === 'course' ? (
              <div className="mb-5">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Select Course</label>
                <select
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  autoFocus
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white">
                  <option value="">— Select a course —</option>
                  {COURSE_OPTIONS.map(c => (
                    <option key={c.key} value={c.label}>{c.label}</option>
                  ))}
                </select>
                {modal.student.course && (
                  <p className="text-xs text-gray-400 mt-1.5">Current: {modal.student.course}</p>
                )}
              </div>
            ) : (
              <div className="mb-5">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  {modal.mode === 'email' ? 'New Email Address' : 'Phone Number'}
                </label>
                <input
                  type={modal.mode === 'email' ? 'email' : 'tel'}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSave()}
                  placeholder={modal.mode === 'email' ? 'student@email.com' : '9876543210'}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  autoFocus
                />
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={closeModal}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-opacity disabled:opacity-60"
                style={
                  modal.mode === 'password'
                    ? { backgroundColor: '#F0B429', color: '#2b2240' }
                    : { background: 'linear-gradient(to right, #2b2240, #4e3a69)', color: 'white' }
                }>
                {saving ? 'Saving…' : modal.mode === 'password' ? 'Confirm Reset' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </TrackerLayout>
  );
};

export default StudentLoginManager;
